import { useIntl } from "react-intl";
import { FieldHelperProps, FieldInputProps, FieldMetaProps } from "formik";
import { Box, ClassNameMap, Typography } from "@mui/material";

import { TooltipLabel } from "../../components/Tooltips";
import { EditFormDetails } from "../interfaces";
import TextToggled from "./TextToggled";

/**
 * A read-only field whose value is clamped until the reader expands it.
 *
 * ⚠️ Not a `TextField`. The value is never editable here, and an outlined input
 * that refuses every keystroke reads as broken rather than as read-only — so this
 * renders the label and a bordered panel, matching `TextAreaReadOnly` in
 * `AcceptSuggestionModal`. The two show the same `description` value, and the
 * clamping behaviour is shared through `TextToggled` so they cannot drift.
 */
const TextToggledField = ({
  field,
  filter,
}: {
  // ⚠️ The full field contract even though a read-only field uses none of
  // `classes`, `helpers` or `meta`: `FormBuilder` passes all five to every field,
  // and narrowing the type here fails the call site rather than simplifying it.
  classes: ClassNameMap;
  field: FieldInputProps<any>;
  filter: EditFormDetails;
  helpers: FieldHelperProps<any>;
  meta: FieldMetaProps<any>;
}) => {
  const intl = useIntl();
  const label = intl.formatMessage({ id: filter.label });

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {filter.tooltip ? (
          <TooltipLabel
            label={label}
            tooltipLabel={intl.formatMessage(
              { id: filter.tooltip },
              { br: <br /> }
            )}
            translateTooltip={false}
          />
        ) : (
          label
        )}
      </Typography>
      <Box
        sx={{
          borderColor: "background.input",
          borderWidth: 1,
          borderStyle: "solid",
          borderRadius: 1,
          p: 1,
          mt: 0.5,
        }}
      >
        <TextToggled
          text={field.value == null ? "" : String(field.value)}
          sx={{ color: "text.secondary" }}
        />
      </Box>
    </Box>
  );
};

export default TextToggledField;
